"use client";

import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AtSign,
  Bold,
  Highlighter,
  ImageIcon,
  Italic,
  Link,
  List,
  ListOrdered,
  Paperclip,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Trash2,
  Underline,
  Undo2,
} from "lucide-react";
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import type { ForumActor, ForumAttachment } from "../types";
import {
  listForumMentionUsers,
  uploadForumAttachment,
} from "../services/forum-api";

export type ForumUploadContext = {
  commentId?: string;
  draftId?: string;
  postId?: string;
  scope: "POST" | "COMMENT";
};

export type RichTextInlineImage = {
  id: string;
  originalFileName: string;
  url: string;
};

export type RichTextAttachment = RichTextInlineImage & {
  inline: boolean;
  mimeType: string;
  sizeBytes: number;
};

function createUploadId() {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `upload-${uuid}`;
}

export function ForumRichEditor({
  disabled,
  minHeightClassName = "min-h-36",
  onAttachmentUploaded,
  onAttachmentUpload,
  onChange,
  onSubmitShortcut,
  placeholder,
  mode = "forum",
  onImageUpload,
  uploadContext,
  value,
}: {
  disabled?: boolean;
  minHeightClassName?: string;
  onAttachmentUploaded?: (attachment: ForumAttachment) => void;
  onAttachmentUpload?: (file: File) => Promise<RichTextAttachment>;
  onImageUpload?: (file: File) => Promise<RichTextInlineImage>;
  onChange: (value: string) => void;
  onSubmitShortcut?: () => void;
  placeholder: string;
  mode?: "forum" | "text-only";
  uploadContext?: ForumUploadContext;
  value: string;
}) {
  const textOnly = mode === "text-only";
  const attachmentAttribute =
    onImageUpload || onAttachmentUpload
      ? "data-system-announcement-attachment-id"
      : "data-forum-attachment-id";
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorShellRef = useRef<HTMLDivElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const savedSelectionRef = useRef<Range | null>(null);
  const resizeStateRef = useRef<{
    image: HTMLImageElement;
    startWidth: number;
    startX: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null);
  const [imageHandle, setImageHandle] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [linkModal, setLinkModal] = useState<{
    href: string;
    text: string;
  } | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionUsers, setMentionUsers] = useState<ForumActor[]>([]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  useEffect(() => {
    updateImageHandle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageId]);

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", resizeImage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (mentionQuery === null) {
      return;
    }

    const timeout = window.setTimeout(() => {
      void listForumMentionUsers(mentionQuery).then((users) => {
        if (!cancelled) setMentionUsers(users);
      });
    }, 180);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [mentionQuery]);

  function exec(command: string, valueArg?: string) {
    editorRef.current?.focus();
    document.execCommand(command, false, valueArg);
    emitChange();
  }

  function emitChange() {
    const html = editorRef.current?.innerHTML ?? "";
    setUploadError(null);
    onChange(html);
    detectMentionQuery();
    updateImageHandle();
  }

  function handleEditorClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (target instanceof HTMLImageElement) {
      const attachmentId = target.getAttribute(attachmentAttribute);
      setSelectedImageId(attachmentId);
      setSelectedAttachmentId(attachmentId);
      window.setTimeout(updateImageHandle, 0);
      return;
    }

    const attachment =
      target instanceof Element
        ? target.closest("[data-system-announcement-attachment-id]")
        : null;
    if (attachment) {
      setSelectedAttachmentId(
        attachment.getAttribute("data-system-announcement-attachment-id"),
      );
      return;
    }

    setSelectedImageId(null);
    setSelectedAttachmentId(null);
    setImageHandle(null);
  }

  function removeSelectedAttachment() {
    if (!selectedAttachmentId) return;
    const attachment = editorRef.current?.querySelector(
      `[data-system-announcement-attachment-id="${selectedAttachmentId}"]`,
    );
    if (!attachment || disabled || uploading) return;
    attachment.remove();
    setSelectedAttachmentId(null);
    setSelectedImageId(null);
    setImageHandle(null);
    emitChange();
  }

  function detectMentionQuery() {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    const offset = selection?.anchorOffset ?? 0;
    const text =
      node?.nodeType === Node.TEXT_NODE
        ? (node.textContent ?? "").slice(0, offset)
        : "";
    const match = /(?:^|\s)@([\p{L}\p{N}._-]{0,32})$/u.exec(text);
    if (match) {
      setMentionQuery(match[1]);
      return;
    }

    setMentionQuery(null);
    setMentionUsers([]);
  }

  function openLinkModal() {
    const selection = window.getSelection();
    let range =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    savedSelectionRef.current = range?.cloneRange() ?? null;

    const link = findClosestLink(selection?.anchorNode ?? null);
    if (link) {
      range = document.createRange();
      range.selectNode(link);
      savedSelectionRef.current = range;
    }

    setLinkModal({
      href: link?.getAttribute("href") ?? "",
      text: selection?.toString() || link?.textContent || "",
    });
  }

  function closeLinkModal() {
    setLinkModal(null);
    savedSelectionRef.current = null;
  }

  function insertLink() {
    if (!linkModal) return;

    const href = normalizeHref(linkModal.href);
    const text = linkModal.text.trim() || href || "";
    if (!href || !text) return;

    restoreSelection(savedSelectionRef.current);
    exec(
      "insertHTML",
      `<a href="${escapeAttribute(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(
        text,
      )}</a>`,
    );
    closeLinkModal();
  }

  function removeLink() {
    exec("unlink");
  }

  async function uploadFile(file: File, inline: boolean) {
    if (!uploadContext && !onAttachmentUpload && !(inline && onImageUpload))
      return;
    const temporaryId = createUploadId();
    const previewUrl = inline ? URL.createObjectURL(file) : null;
    insertTemporaryNode({ file, inline, previewUrl, temporaryId });
    setUploading(true);
    setUploadError(null);
    try {
      const attachment = inline
        ? onImageUpload
          ? await onImageUpload(file)
          : await uploadForumAttachment({ ...uploadContext!, file, inline })
        : onAttachmentUpload
          ? await onAttachmentUpload(file)
          : await uploadForumAttachment({ ...uploadContext!, file, inline });
      if (!onImageUpload) onAttachmentUploaded?.(attachment as ForumAttachment);

      replaceTemporaryNode(
        temporaryId,
        attachment as RichTextAttachment,
        inline,
      );
    } catch (error) {
      setUploadError(errorMessage(error, "Não foi possível enviar o arquivo."));
      markTemporaryNodeFailed(temporaryId);
    } finally {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setUploading(false);
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }

  function insertTemporaryNode({
    file,
    inline,
    previewUrl,
    temporaryId,
  }: {
    file: File;
    inline: boolean;
    previewUrl: string | null;
    temporaryId: string;
  }) {
    const html = inline
      ? `<img src="${previewUrl ?? ""}" alt="Enviando ${escapeAttribute(file.name)}" data-rich-text-upload-id="${temporaryId}" data-uploading="true" style="width: 480px; height: auto;">`
      : `<div contenteditable="false" draggable="true" data-rich-text-upload-id="${temporaryId}" data-system-announcement-attachment="true" class="rich-text-attachment rich-text-attachment-uploading">Enviando ${escapeHtml(file.name)}...</div><p><br></p>`;
    exec("insertHTML", html);
  }

  function replaceTemporaryNode(
    temporaryId: string,
    attachment: RichTextAttachment,
    inline: boolean,
  ) {
    const temporary = editorRef.current?.querySelector(
      `[data-rich-text-upload-id="${temporaryId}"]`,
    );
    if (!temporary) return;
    const replacement = document.createElement(inline ? "img" : "div");
    if (inline) {
      const image = replacement as HTMLImageElement;
      image.src = attachment.url;
      image.alt = attachment.originalFileName;
      image.setAttribute(attachmentAttribute, attachment.id);
      image.style.width = "480px";
      image.style.height = "auto";
    } else {
      replacement.setAttribute("contenteditable", "false");
      replacement.setAttribute("draggable", "true");
      replacement.setAttribute("data-system-announcement-attachment", "true");
      replacement.setAttribute(
        "data-system-announcement-attachment-id",
        attachment.id,
      );
      replacement.className = "rich-text-attachment";
      replacement.innerHTML = `<strong>📎 ${escapeHtml(attachment.originalFileName)}</strong><span>${escapeHtml(attachment.mimeType)} · ${formatBytes(attachment.sizeBytes)}</span>`;
    }
    temporary.replaceWith(replacement);
    emitChange();
  }

  function markTemporaryNodeFailed(temporaryId: string) {
    const temporary = editorRef.current?.querySelector(
      `[data-rich-text-upload-id="${temporaryId}"]`,
    );
    if (!temporary) return;
    temporary.setAttribute("data-upload-error", "true");
    if (!(temporary instanceof HTMLImageElement)) {
      temporary.textContent =
        "Falha no envio. Remova este bloco ou tente novamente.";
    }
    emitChange();
  }

  function insertMention(user: ForumActor) {
    removeMentionTrigger();
    exec(
      "insertHTML",
      `<span class="forum-mention" data-mention-user-id="${user.id}">@${escapeHtml(
        user.name,
      )}</span>&nbsp;`,
    );
    setMentionQuery(null);
    setMentionUsers([]);
  }

  function removeMentionTrigger() {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    const offset = selection?.anchorOffset ?? 0;
    if (!selection || node?.nodeType !== Node.TEXT_NODE) return;

    const text = node.textContent ?? "";
    const beforeCursor = text.slice(0, offset);
    const afterCursor = text.slice(offset);
    const nextBeforeCursor = beforeCursor.replace(
      /(^|\s)@[\p{L}\p{N}._-]{0,32}$/u,
      "$1",
    );

    if (nextBeforeCursor === beforeCursor) return;

    node.textContent = `${nextBeforeCursor}${afterCursor}`;
    const range = document.createRange();
    range.setStart(node, nextBeforeCursor.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  function updateImageHandle() {
    const image = findSelectedImage(
      editorRef.current,
      selectedImageId,
      attachmentAttribute,
    );
    const shell = editorShellRef.current;
    if (!image || !shell) {
      setImageHandle(null);
      return;
    }

    const imageRect = image.getBoundingClientRect();
    const shellRect = shell.getBoundingClientRect();
    setImageHandle({
      left: imageRect.right - shellRect.left - 10,
      top: imageRect.bottom - shellRect.top - 10,
    });
  }

  function startImageResize(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    const image = findSelectedImage(
      editorRef.current,
      selectedImageId,
      attachmentAttribute,
    );
    if (!image || disabled || uploading) return;

    resizeStateRef.current = {
      image,
      startWidth: image.getBoundingClientRect().width,
      startX: event.clientX,
    };

    window.addEventListener("mousemove", resizeImage);
    window.addEventListener("mouseup", stopImageResize, { once: true });
  }

  function resizeImage(event: globalThis.MouseEvent) {
    const state = resizeStateRef.current;
    const editor = editorRef.current;
    if (!state || !editor) return;

    const editorWidth = editor.getBoundingClientRect().width - 24;
    const width = Math.max(
      120,
      Math.min(
        Math.round(state.startWidth + event.clientX - state.startX),
        editorWidth,
      ),
    );
    state.image.style.width = `${width}px`;
    state.image.style.height = "auto";
    updateImageHandle();
  }

  function stopImageResize() {
    window.removeEventListener("mousemove", resizeImage);
    resizeStateRef.current = null;
    emitChange();
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 p-2 dark:border-slate-800">
        <ToolbarButton
          disabled={disabled || uploading}
          label="Desfazer"
          onClick={() => exec("undo")}
        >
          <Undo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Refazer"
          onClick={() => exec("redo")}
        >
          <Redo2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Negrito"
          onClick={() => exec("bold")}
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Italico"
          onClick={() => exec("italic")}
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Sublinhado"
          onClick={() => exec("underline")}
        >
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        {!textOnly ? (
          <ToolbarButton
            disabled={disabled || uploading}
            label="Tachado"
            onClick={() => exec("strikeThrough")}
          >
            <Strikethrough className="h-4 w-4" />
          </ToolbarButton>
        ) : null}
        <ToolbarButton
          disabled={disabled || uploading}
          label="Lista"
          onClick={() => exec("insertUnorderedList")}
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Lista numerada"
          onClick={() => exec("insertOrderedList")}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        {!textOnly ? (
          <>
            <ToolbarButton
              disabled={disabled || uploading}
              label="Alinhar à esquerda"
              onClick={() => exec("justifyLeft")}
            >
              <AlignLeft className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              disabled={disabled || uploading}
              label="Centralizar"
              onClick={() => exec("justifyCenter")}
            >
              <AlignCenter className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              disabled={disabled || uploading}
              label="Alinhar à direita"
              onClick={() => exec("justifyRight")}
            >
              <AlignRight className="h-4 w-4" />
            </ToolbarButton>
          </>
        ) : null}
        <ToolbarButton
          disabled={disabled || uploading}
          label="Link"
          onClick={openLinkModal}
        >
          <Link className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || uploading}
          label="Remover link"
          onClick={removeLink}
        >
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
        {!textOnly ? (
          <>
            <label
              aria-label="Anexar arquivo"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900"
            >
              <Paperclip className="h-4 w-4" />
              <input
                ref={attachmentInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,text/plain"
                className="hidden"
                disabled={disabled || uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadFile(file, false);
                }}
              />
            </label>
            <label
              aria-label="Inserir imagem"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900"
            >
              <ImageIcon className="h-4 w-4" />
              <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                disabled={disabled || uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadFile(file, true);
                }}
              />
            </label>
            <ToolbarButton
              disabled={disabled || uploading}
              label="Mencionar"
              onClick={() => setMentionQuery(mentionQuery ?? "")}
            >
              <AtSign className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton
              disabled={disabled || uploading || !selectedAttachmentId}
              label="Remover anexo selecionado"
              onClick={removeSelectedAttachment}
            >
              <Trash2 className="h-4 w-4" />
            </ToolbarButton>
            <label className="ml-auto flex h-8 items-center gap-1 rounded-md border border-slate-200 px-2 text-xs text-slate-500 dark:border-slate-800">
              <span>Cor</span>
              <input
                type="color"
                className="h-5 w-6 bg-transparent"
                disabled={disabled}
                onChange={(event) => exec("foreColor", event.target.value)}
              />
            </label>
            <label className="flex h-8 items-center gap-1 rounded-md border border-slate-200 px-2 text-xs text-slate-500 dark:border-slate-800">
              <Highlighter className="h-3.5 w-3.5" />
              <input
                type="color"
                className="h-5 w-6 bg-transparent"
                disabled={disabled}
                onChange={(event) => exec("backColor", event.target.value)}
              />
            </label>
          </>
        ) : null}
      </div>
      <div ref={editorShellRef} className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder}
          className={[
            "forum-rich-editor max-h-[42dvh] overflow-y-auto px-3 py-2 text-sm leading-6 outline-none empty:before:text-slate-400 empty:before:content-[attr(data-placeholder)] dark:text-slate-100",
            minHeightClassName,
          ].join(" ")}
          onClick={handleEditorClick}
          onInput={emitChange}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
              event.preventDefault();
              onSubmitShortcut?.();
            }
          }}
          onKeyUp={detectMentionQuery}
          onPaste={
            textOnly
              ? (event) => {
                  event.preventDefault();
                  const pasted = event.clipboardData.getData("text/plain");
                  document.execCommand("insertText", false, pasted);
                  emitChange();
                }
              : undefined
          }
          onScroll={updateImageHandle}
          onDragOver={(event) => {
            if (!textOnly && event.dataTransfer.files.length)
              event.preventDefault();
          }}
          onDrop={(event) => {
            const file = event.dataTransfer.files?.[0];
            if (!file || textOnly || disabled) return;
            event.preventDefault();
            const range = document.caretRangeFromPoint?.(
              event.clientX,
              event.clientY,
            );
            if (range) {
              const selection = window.getSelection();
              selection?.removeAllRanges();
              selection?.addRange(range);
            }
            void uploadFile(file, ANNOUNCEMENT_IMAGE_TYPES.has(file.type));
          }}
          suppressContentEditableWarning
        />
        {selectedImageId && imageHandle ? (
          <button
            type="button"
            aria-label="Redimensionar imagem"
            className="absolute z-20 h-5 w-5 cursor-se-resize rounded border border-sky-500 bg-white shadow-sm dark:bg-slate-950"
            disabled={disabled || uploading}
            onMouseDown={startImageResize}
            style={{ left: imageHandle.left, top: imageHandle.top }}
          />
        ) : null}
        {linkModal ? (
          <div className="absolute left-3 top-3 z-30 w-80 rounded-md border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-800 dark:bg-slate-950">
            <div className="grid gap-2">
              <label className="grid gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                Texto do link
                <input
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-normal text-slate-950 outline-none focus:border-sky-500 dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={linkModal.text}
                  onChange={(event) =>
                    setLinkModal((current) =>
                      current
                        ? { ...current, text: event.target.value }
                        : current,
                    )
                  }
                  autoFocus
                />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                URL
                <input
                  className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-normal text-slate-950 outline-none focus:border-sky-500 dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                  value={linkModal.href}
                  onChange={(event) =>
                    setLinkModal((current) =>
                      current
                        ? { ...current, href: event.target.value }
                        : current,
                    )
                  }
                  placeholder="https://..."
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={closeLinkModal}
                >
                  Cancelar
                </Button>
                <Button type="button" size="sm" onClick={insertLink}>
                  Inserir
                </Button>
              </div>
            </div>
          </div>
        ) : null}
        {mentionQuery !== null && mentionUsers.length ? (
          <div className="absolute bottom-2 left-3 z-20 w-72 overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-800 dark:bg-slate-950">
            {mentionUsers.map((user) => (
              <button
                key={user.id}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-900"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insertMention(user)}
              >
                <span className="block font-medium text-slate-900 dark:text-white">
                  {user.name}
                </span>
                <span className="block text-xs text-slate-500">
                  {user.email}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {uploading ? (
          <div className="absolute inset-x-0 bottom-0 bg-slate-950/70 px-3 py-1 text-xs text-white">
            Enviando arquivo...
          </div>
        ) : null}
      </div>
      {uploadError ? (
        <p className="border-t border-red-100 px-3 py-2 text-xs text-red-700 dark:border-red-950 dark:text-red-300">
          {uploadError}
        </p>
      ) : null}
    </div>
  );
}

export function ForumRichContent({
  className,
  content,
  contentFormat,
}: {
  className?: string;
  content: string;
  contentFormat?: string;
}) {
  const [imagePreview, setImagePreview] = useState<{
    alt: string;
    src: string;
  } | null>(null);
  const [externalLink, setExternalLink] = useState<string | null>(null);
  const html =
    contentFormat === "html"
      ? content
      : `<p>${escapeHtml(content).replace(/\n/g, "<br>")}</p>`;

  function handleContentClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;

    const attachment =
      target instanceof Element
        ? target.closest("[data-system-announcement-attachment-id]")
        : null;
    const attachmentId = attachment?.getAttribute(
      "data-system-announcement-attachment-id",
    );
    if (
      attachmentId &&
      attachment?.getAttribute("data-system-announcement-attachment") === "true"
    ) {
      event.preventDefault();
      window.open(
        `/api/system/announcements/attachments/${encodeURIComponent(attachmentId)}/file`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }

    if (target instanceof HTMLImageElement) {
      event.preventDefault();
      setImagePreview({
        alt: target.alt || "Imagem da publicação",
        src: target.src,
      });
      return;
    }

    const link = findClosestAnchor(target);
    if (!link) return;

    event.preventDefault();
    const href = link.href || link.getAttribute("href");
    if (!href) return;

    if (isExternalHref(href)) {
      setExternalLink(href);
      return;
    }

    window.open(href, "_blank", "noopener,noreferrer");
  }

  return (
    <>
      <div
        className={[
          "forum-rich-content text-sm leading-6 text-slate-800 dark:text-slate-200",
          className ?? "",
        ].join(" ")}
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={handleContentClick}
      />
      {imagePreview ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4">
          <div className="grid max-h-full max-w-5xl gap-3">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setImagePreview(null)}
              >
                Fechar
              </Button>
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={imagePreview.src}
              alt={imagePreview.alt}
              className="max-h-[82vh] max-w-full rounded-md bg-white object-contain"
            />
          </div>
        </div>
      ) : null}
      {externalLink ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
          <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-800 dark:bg-slate-950">
            <h3 className="text-base font-semibold text-slate-950 dark:text-white">
              Você esta saindo do Nexus
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Esse link abre um ambiente externo ao Nexus. Confira o destino
              antes de continuar e evite informar credenciais em paginas que
              você não reconhece.
            </p>
            <p className="mt-2 break-all rounded bg-slate-50 p-2 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              {externalLink}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExternalLink(null)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  window.open(externalLink, "_blank", "noopener,noreferrer");
                  setExternalLink(null);
                }}
              >
                Abrir em nova aba
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function ToolbarButton({
  children,
  disabled,
  label,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

const ANNOUNCEMENT_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1).replace(".0", "")} MB`;
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function normalizeHref(value: string | null) {
  const href = value?.trim();
  if (!href) return null;
  if (/^(https?:\/\/|mailto:)/i.test(href)) return href;
  if (href.startsWith("/")) return href;
  if (/^[\w.-]+@[\w.-]+\.\w{2,}$/i.test(href)) return `mailto:${href}`;
  return `https://${href}`;
}

function restoreSelection(range: Range | null) {
  if (!range) return;
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

function findClosestLink(node: Node | null) {
  const element =
    node instanceof Element ? node : (node?.parentElement ?? null);
  return element?.closest("a") ?? null;
}

function findClosestAnchor(target: EventTarget) {
  if (!(target instanceof Element)) return null;
  return target.closest("a");
}

function isExternalHref(href: string) {
  if (/^mailto:/i.test(href)) return true;
  try {
    const url = new URL(href, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return true;
  }
}

function findSelectedImage(
  root: HTMLElement | null,
  attachmentId: string | null,
  attribute: string,
) {
  if (!root || !attachmentId) return null;

  return (
    [...root.querySelectorAll("img")].find(
      (image) => image.getAttribute(attribute) === attachmentId,
    ) ?? null
  );
}

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
